import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OrderIndexProvider, toOrderRooftopFacts, readMetricParam, withoutMetricParam } from '@hopdrive/feature-order-index';
import type { OrderIndexConfig, OrderTabActionsContribution } from '@hopdrive/feature-order-index';
import { OrderIndexPlatformProvider } from '@hopdrive/feature-order-index';
import { capabilityGate, usePortalHost, useProductEntry, useShellTabActions } from '@hopdrive/feature-portal-products';
import type { TabActionsContribution } from '@hopdrive/feature-portal-products';

import { ScenarioActionsProvider } from '@components/ScenarioActions';
import { useUnreadMessages } from '@features/moves/hooks/useUnreadMessages';
import { useCustomerConfig, useCustomerConfigLoaded, useRooftop } from '@store';

import { useDealerOrderInteraction } from './DealerOrderInteraction';
import { dealerOrderIndexPlatform } from './shared/platform/useDealerOrderIndexPlatform';
import { useMovesOrderOperationalModel } from './useMovesOrderOperationalModel';

/**
 * The dealer's side of `@hopdrive/feature-order-index`: everything an order surface
 * needs from this app, assembled once and handed down as values.
 *
 * The package cannot import `@hopdrive/feature-portal-products` (features do not import each
 * other), so the two contexts meet here instead — the portal context above, the order-index
 * configuration below. Every surface underneath reads `useOrderIndex()` and knows nothing
 * about the dealer's store, its session, or its shell.
 *
 * `ScenarioActionsProvider` sits above the order-index configuration because it carries the
 * interaction layer it publishes: one provider per mount means one photo viewer, one set of
 * dialogs, one chat for every card below.
 */
const DealerOrderIndexConfig: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { viewer } = usePortalHost();
  const rooftopRecord = useRooftop();
  const configLoaded = useCustomerConfigLoaded();
  const referenceNumLabel = (useCustomerConfig()?.reference_num_label as string | null | undefined) ?? null;
  const interaction = useDealerOrderInteraction();

  // Unread is an id set, not a count: the hook runs its own window and gates itself, so only
  // its set of move ids carries over, as Valet's adapt extras.
  const { unreadMoves } = useUnreadMessages();
  const unreadMoveIds = useMemo(() => new Set(unreadMoves.map(unread => unread.moveId)), [unreadMoves]);
  const adaptExtras = useMemo(() => ({ moves: { unreadMoveIds } }), [unreadMoveIds]);

  // The shell's registration is an effect keyed on the contribution, and the package's tab
  // has no way to call a hook on the app's behalf. So the adapter holds the contribution and
  // publishes a stable setter; the registration itself stays here, inside the shell's frame.
  const [tabActions, setTabActions] = useState<OrderTabActionsContribution | null>(null);
  useShellTabActions(tabActions as TabActionsContribution | null);

  const operationalModel = useMovesOrderOperationalModel();
  const rooftop = useMemo(() => toOrderRooftopFacts(rooftopRecord, operationalModel), [rooftopRecord, operationalModel]);

  // A read_only Moves product (a lapsed contract) shows its orders with no write controls.
  const canManageMoves = capabilityGate(useProductEntry('moves'), 'manageExisting').kind === 'render';
  const writeControlsAllowed = useMemo(() => ({ moves: canManageMoves }), [canManageMoves]);

  // The Orders tab's `?metric=` arrival from a Home figure, read and cleared through this app's router.
  const location = useLocation();
  const navigate = useNavigate();
  const metricDeepLink = useMemo(
    () => ({
      metric: readMetricParam(location.search),
      clear: () => navigate({ pathname: location.pathname, search: withoutMetricParam(location.search), hash: location.hash }, { replace: true }),
    }),
    [location.pathname, location.search, location.hash, navigate]
  );

  const value = useMemo<OrderIndexConfig>(
    () => ({
      rooftop,
      configLoaded,
      referenceNumLabel,
      viewer: { role: viewer.role, isAdminViewer: viewer.isAdminViewer, customerId: viewer.customerId ?? null },
      platform: dealerOrderIndexPlatform,
      interaction,
      adaptExtras,
      writeControlsAllowed,
      metricDeepLink,
      registerTabActions: setTabActions,
      // The offer report lives at this app's route; the tab decides whether the rooftop's tier earns the entry.
      openAssignmentsReport: () => navigate('/view-assignments'),
    }),
    [rooftop, configLoaded, referenceNumLabel, viewer.role, viewer.isAdminViewer, viewer.customerId, interaction, adaptExtras, writeControlsAllowed, metricDeepLink, navigate]
  );

  return (
    <OrderIndexProvider value={value}>
      <OrderIndexPlatformProvider platform={dealerOrderIndexPlatform}>{children}</OrderIndexPlatformProvider>
    </OrderIndexProvider>
  );
};

/** Composes the dealer interaction layer and configuration around a shared order surface. */
const DealerOrderIndexAdapter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ScenarioActionsProvider>
    <DealerOrderIndexConfig>{children}</DealerOrderIndexConfig>
  </ScenarioActionsProvider>
);

export default DealerOrderIndexAdapter;
